import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent } from "@/ui/atoms";
import { FileUploadForm } from "@/ui/organisms";
import { CheckCircle, ShieldX, X } from "lucide-react";
import { EMPTY_ARRAY } from "@/utils/utils";

type TDealOption = {
    id: string;
    name: string;
    code: string;
    isLive?: boolean;
    acceptsOtb?: boolean;
};

const ALLOWED_FILE_TYPES = ["trial_balance", "general_ledger", "tb_gl"] as const;
type TAllowedFileType = (typeof ALLOWED_FILE_TYPES)[number];

const PERIOD_PATTERN = /^\d{4}-\d{2}$/;

/**
 * Read the Homepage v2 "Upload now" / "Not yet due" deep-link params
 * (`?dealId=…&period=YYYY-MM-DD&fileType=trial_balance`, design doc §7.5).
 * Never trusted blindly: an unparseable or disallowed value is dropped
 * rather than passed through — scope is still enforced by `deals` (the
 * caller's own accessible list) and by `allowedFileTypes` on the form below,
 * this is just about not seeding the form with garbage.
 */
function useUploadDeepLink(): { dealId?: string; period?: string; fileType?: TAllowedFileType } {
    const [searchParams] = useSearchParams();
    return useMemo(() => {
        const dealId = searchParams.get("dealId") ?? undefined;

        const rawPeriod = searchParams.get("period"); // "YYYY-MM-DD"
        const period =
            rawPeriod && PERIOD_PATTERN.test(rawPeriod.slice(0, 7))
                ? rawPeriod.slice(0, 7)
                : undefined;

        const rawFileType = searchParams.get("fileType");
        const fileType = (ALLOWED_FILE_TYPES as readonly string[]).includes(rawFileType ?? "")
            ? (rawFileType as TAllowedFileType)
            : undefined;

        return { dealId, period, fileType };
    }, [searchParams]);
}

/**
 * View parameters (injected dependencies)
 */
export type TViewUploadTBGLParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useDeals: () => { data: TDealOption[] | null; loading: boolean };
        onUploadFile: (data: {
            dealId: string;
            period: string;
            fileType: string;
            file: File;
        }) => Promise<void>;
        canUpload: boolean;
        /**
         * Whether the user can view the TB/GL files list. When false, the user
         * stays on the upload page after a successful upload (instead of being
         * redirected to a page they cannot access) and sees a success toast.
         */
        canViewFiles: boolean;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewUploadTBGLProps = Record<string, never>;

/**
 * Creates the Upload TB/GL view component
 */
export const createViewUploadTBGL = ({ useCases }: TViewUploadTBGLParams) => {
    const ViewUploadTBGL: React.FC<TViewUploadTBGLProps> = () => {
        const { onMount, onUnmount, useDeals, onUploadFile, canUpload, canViewFiles } = useCases();
        const navigate = useNavigate();
        const deepLink = useUploadDeepLink();

        const dealsState = useDeals();
        const [showSuccessToast, setShowSuccessToast] = useState(false);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        useEffect(() => {
            if (!showSuccessToast) return;
            const timer = setTimeout(() => setShowSuccessToast(false), 5000);
            return () => clearTimeout(timer);
        }, [showSuccessToast]);

        const handleSuccess = () => {
            if (canViewFiles) {
                // "/" lands on the board where the submission this upload
                // satisfies moves out of "Upload now", or — for a company
                // still on the legacy pages — on the TB/GL list, without this
                // view having to know which.
                navigate("/");
                return;
            }
            // The user can upload but cannot see a submissions view, so stay on
            // the upload page and confirm the upload with a success toast.
            setShowSuccessToast(true);
        };

        if (!canUpload) {
            return (
                <PageLayout
                    title="Upload Financial TB/GL"
                    description="Upload Trial Balance, General Ledger, or Opening Trial Balance files"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="mb-4 rounded-full bg-muted p-6">
                                    <ShieldX className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">Upload Not Available</h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    Financial TB/GL upload is only available for Business Partners
                                    with upload permissions enabled.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Upload Financial TB/GL"
                description="Upload Trial Balance, General Ledger, or Opening Trial Balance files"
            >
                <Card className="max-w-2xl">
                    <CardContent className="pt-6">
                        <FileUploadForm
                            allowedFileTypes={["trial_balance", "general_ledger", "tb_gl"]}
                            preselectedFileType={deepLink.fileType ?? "trial_balance"}
                            preselectedDealId={deepLink.dealId}
                            preselectedPeriod={deepLink.period}
                            deals={dealsState.data ?? EMPTY_ARRAY}
                            onUploadFile={onUploadFile}
                            onSuccess={handleSuccess}
                            showCancel={false}
                        />
                    </CardContent>
                </Card>

                {showSuccessToast && (
                    <div
                        role="status"
                        aria-live="polite"
                        className="fixed bottom-6 right-6 z-50 flex max-w-sm items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-lg"
                    >
                        <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                        <span className="flex-1 font-medium">File uploaded successfully.</span>
                        <button
                            type="button"
                            onClick={() => setShowSuccessToast(false)}
                            className="rounded p-1 text-emerald-600 hover:bg-emerald-100"
                            aria-label="Dismiss notification"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </PageLayout>
        );
    };

    return ViewUploadTBGL;
};
